import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export type TimezoneMode = 'local' | 'utc';

export const TIMEZONE_STORAGE_KEY = 'corebtc_crm_timezone';
export const DEFAULT_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export function getStoredTimezoneMode(): TimezoneMode {
  if (typeof window === 'undefined') {
    return 'local';
  }
  return localStorage.getItem(TIMEZONE_STORAGE_KEY) === 'utc' ? 'utc' : 'local';
}

export function setStoredTimezoneMode(mode: TimezoneMode) {
  localStorage.setItem(TIMEZONE_STORAGE_KEY, mode);
}

export function parseBackendDate(value: unknown): Dayjs | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = dayjs(value as string | number | Date);
  return parsed.isValid() ? parsed : null;
}

export function formatDateTime(
  value: unknown,
  format = DEFAULT_DATETIME_FORMAT,
  mode?: TimezoneMode,
): string {
  const parsed = parseBackendDate(value);
  if (!parsed) {
    return '-';
  }
  const timezoneMode = mode ?? getStoredTimezoneMode();
  if (timezoneMode === 'utc') {
    return dayjs.utc(parsed.valueOf()).format(format);
  }
  return parsed.format(format);
}

export function toPickerDisplay(
  value: unknown,
  mode?: TimezoneMode,
): Dayjs | null {
  const parsed = parseBackendDate(value);
  if (!parsed) {
    return null;
  }
  const timezoneMode = mode ?? getStoredTimezoneMode();
  if (timezoneMode === 'utc') {
    const utcDate = dayjs.utc(parsed.valueOf());
    return dayjs(utcDate.format(DEFAULT_DATETIME_FORMAT));
  }
  return parsed;
}

export function fromPickerDisplay(
  value: Dayjs | null | undefined,
  mode?: TimezoneMode,
): Dayjs | null {
  if (!value || !dayjs.isDayjs(value)) {
    return null;
  }
  const timezoneMode = mode ?? getStoredTimezoneMode();
  if (timezoneMode === 'utc') {
    return dayjs.utc(value.format(DEFAULT_DATETIME_FORMAT));
  }
  return value;
}

export function pickerValueToUtcIso(
  value: Dayjs | null | undefined,
  mode?: TimezoneMode,
): string | undefined {
  const converted = fromPickerDisplay(value, mode);
  return converted?.toISOString();
}

export function normalizeRangeForApi(
  range: unknown,
  mode?: TimezoneMode,
): { start?: string; end?: string } {
  if (!range || !Array.isArray(range)) {
    return {};
  }
  const [start, end] = range;
  const toIso = (input: unknown) => {
    if (!input) {
      return undefined;
    }
    if (dayjs.isDayjs(input)) {
      return pickerValueToUtcIso(input, mode);
    }
    if (typeof input === 'string') {
      return parseBackendDate(input)?.toISOString();
    }
    return undefined;
  };
  return {
    start: toIso(start),
    end: toIso(end),
  };
}

export function nowInTimezone(mode?: TimezoneMode): Dayjs {
  const timezoneMode = mode ?? getStoredTimezoneMode();
  if (timezoneMode === 'utc') {
    const utcNow = dayjs.utc();
    return dayjs(utcNow.format(DEFAULT_DATETIME_FORMAT));
  }
  return dayjs();
}
